import { type BackendSessionType } from "./types";
import { FILTER_BY_SESSION } from "../../dependencies/env";
import type { Extends } from "../../types";

type QuerySessionType = Extends<
  BackendSessionType,
  "BREAKFAST" | "DINNER" | "DINNER2" | "LUNCH"
>;

const querySessionsType: { [sessionType in SessionType]: QuerySessionType } = {
  Breakfast: "BREAKFAST",
  Lunch: "LUNCH",
  Dinner: "DINNER",
  Dinner2: "DINNER2",
};

/**
 * Maps one or more {@link SessionType}s to the backend `SessionTypes` enum
 * values used by `allRestaurants` filters (`sessions`, `offersSession`,
 * `offersFirstTableSession`, `offersWalkInSession`).
 *
 * - Dinner expands to `DINNER` + `DINNER2` so Last Table inventory is included.
 * - When `hasLastTable` is true and any dinner session is present, only
 *   `DINNER2` is returned.
 * - Returns `undefined` when session filtering is disabled or `sessions` is empty.
 */
export default function getQuerySessions(
  sessions: SessionType[],
  hasLastTable = false,
) {
  // don't apply the session availability filter when it's disabled
  if (!FILTER_BY_SESSION || sessions.length === 0) {
    return;
  }

  // apply the session filter when the "Last Table" filter is active
  if (
    hasLastTable &&
    sessions.some((session) => session === "Dinner" || session === "Dinner2")
  ) {
    return [querySessionsType.Dinner2];
  }

  const querySessions = new Set<QuerySessionType>();
  for (const session of sessions) {
    // get both dinner sessions
    if (session === "Dinner") {
      querySessions.add(querySessionsType.Dinner);
      querySessions.add(querySessionsType.Dinner2);
      continue;
    }

    querySessions.add(querySessionsType[session]);
  }

  return [...querySessions];
}
